"use client";

import { faCheck, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { filter } from "fuzzy";
import { DropdownMenu } from "radix-ui";
import { type ChangeEvent, useDeferredValue, useMemo, useState } from "react";
import { NativeInput, RoundedButton } from "../..";

type Aggregation = {
	label: string;
	value: string;
	options: {
		value: string;
		label: string;
		count?: number;
	}[];
};

type AggregationOption = Aggregation["options"][number];

type AggregationInput = { value: string; options: string[] };

interface FilterButtonProps {
	aggregation: Aggregation;
	searchMessage: string;
	emptyMessage: string;
	onOptionChange: React.Dispatch<React.SetStateAction<AggregationInput[]>>;
}

export function FilterButton(props: FilterButtonProps) {
	const { aggregation, searchMessage, emptyMessage, onOptionChange } = props;
	const [activeOptions, setActiveOptions] = useState<AggregationOption["value"][]>([]);
	const [search, setSearch] = useState("");
	const query = useDeferredValue(search);

	function onActiveOptionChange(option: AggregationOption) {
		const newActiveOptions = activeOptions.includes(option.value)
			? activeOptions.filter((value) => value !== option.value)
			: [...activeOptions, option.value];

		setActiveOptions(newActiveOptions);

		onOptionChange((prev) => {
			if (prev?.some((item) => item.value === aggregation.value)) {
				if (newActiveOptions.length === 0) {
					return prev?.filter((item) => item.value !== aggregation.value);
				}

				return prev?.map((item) =>
					item.value === aggregation.value
						? { value: item.value, options: newActiveOptions }
						: item,
				);
			}

			return [...prev, { value: aggregation.value, options: newActiveOptions }];
		});
	}

	function onSearchChange(evt: ChangeEvent<HTMLInputElement>) {
		setSearch(evt.target.value);
	}

	const options = useMemo(() => {
		const filtered = filter(query, aggregation.options, {
			pre: "<b>",
			post: "</b>",
			extract: (option) => option.label,
		});

		return filtered.map((result) => ({
			...(aggregation.options[result.index] as AggregationOption),
			label: result.string,
		}));
	}, [query, aggregation.options]);

	if (aggregation.options.length === 0) {
		return null;
	}

	return (
		<>
			<DropdownMenu.Root>
				<DropdownMenu.Trigger asChild>
					<RoundedButton>
						{aggregation.label}

						{activeOptions.length > 0 && (
							<span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-dark text-white text-xs">
								{activeOptions.length}
							</span>
						)}

						<FontAwesomeIcon icon={faChevronDown} className="ml-auto h-4 w-4" />
					</RoundedButton>
				</DropdownMenu.Trigger>

				<DropdownMenu.Portal>
					<DropdownMenu.Content className="scrollbar-none flex max-h-96 min-w-[300px] flex-col gap-4 overflow-y-scroll rounded bg-white p-4 shadow">
						{aggregation.options.length > 5 && (
							<NativeInput
								placeholder={`${searchMessage} ${aggregation.label.toLowerCase()}...`}
								aria-label={`${searchMessage} ${aggregation.label.toLowerCase()}...`}
								type="text"
								value={search}
								onChange={onSearchChange}
								onKeyDown={(e) => e.stopPropagation()}
							/>
						)}

						{options.length === 0 ? (
							<DropdownMenu.Item>{emptyMessage}</DropdownMenu.Item>
						) : (
							options.map((option) => (
								<DropdownMenu.Item key={option.value} asChild>
									<button
										type="button"
										className="group flex gap-2 focus:outline-none"
										onClick={() => onActiveOptionChange(option)}
									>
										<span
											className="mr-4 flex-grow text-left group-hover:underline group-focus:underline"
											dangerouslySetInnerHTML={{ __html: option.label }}
										/>

										{!!option.count && (
											<span className="text-shades-medium text-sm">{option.count}</span>
										)}

										<div className="ml-4 flex h-6 w-6 items-start overflow-hidden rounded border border-shades">
											{activeOptions.includes(option.value) && (
												<div className="flex h-full w-full items-center justify-center bg-primary-medium">
													<FontAwesomeIcon
														size="sm"
														icon={faCheck}
														className="block h-5 w-5 text-white"
													/>
												</div>
											)}
										</div>
									</button>
								</DropdownMenu.Item>
							))
						)}
					</DropdownMenu.Content>
				</DropdownMenu.Portal>
			</DropdownMenu.Root>
		</>
	);
}
